import { TLiftedFormModel, TDropdownItem, TLiftedInputTypes } from './form.model';
import { PIPELINE_FORM } from './form.pipeline';
import { TASK_FORM } from './form.tasks';

export const EMPTY_INPUT: TLiftedInputTypes = {
  input: { type: 'EMPTY' },
  metadata: {
    keyName: 'EMPTY',
  },
};

export const FORM_MODIFIER = {
  /**
   * Sets the 'isTouched' key to true and validates the input
   * @param prevLiftedFormModel
   * @param updatedInput
   * @param index
   * @returns formModel
   */
  updateInputByIndex<T>(
    prevLiftedFormModel: TLiftedFormModel<T>,
    updatedInput: TLiftedFormModel<T>['inputs'][number],
    index: number,
  ): TLiftedFormModel<T> {
    const { inputs } = prevLiftedFormModel;
    const updatedInputs = inputs.map((val, i) => {
      if (i === index) {
        return TASK_FORM.updateInputForOtherOption({
          ...updatedInput,
          metadata: { ...updatedInput.metadata, isTouched: true, isActive: true },
        });
      } else {
        return {
          ...val,
          metadata: { ...val.metadata, isActive: false },
        };
      }
    });

    const updatedFormModel = {
      ...prevLiftedFormModel,
      inputs: [...updatedInputs],
    };
    return FORM_MODIFIER.runPipelineOnInputUpdate(updatedFormModel);
  },

  generatePayload: <T>(formModel: TLiftedFormModel<T>): TLiftedFormModel<T> => {
    return PIPELINE_FORM.generatePayload(formModel);
  },

  /**
   * Runs the tasks pipeline when an input is changed
   * @param formModel
   * @returns LiftedFormModel
   */
  runPipelineOnInputUpdate<T>(formModel: TLiftedFormModel<T>) {
    return PIPELINE_FORM.runPipelineOnInputUpdate(formModel);
  },

  /**
   * Create Form with Data, and setValues in inputs
   * @param data
   * @param inputs
   * @returns LiftedFormModel
   */
  createFromWithData<T>(data: T, inputs: TLiftedInputTypes<T>[]) {
    return PIPELINE_FORM.setValues({
      inputs,
      data,
    });
  },

  /**
   * Create Empty Form
   * @param inputs
   * @returns LiftedFormModel
   */
  createEmptyForm: <T>(inputs: TLiftedInputTypes<T>[]) => {
    return PIPELINE_FORM.setValues({
      inputs,
    });
  },

  /**
   * Sets noEditsInTable based on the data. It needs to be called before createFromWithData,
   * to enable/disable liftedInput in the edit mode based on the noEditsInTable value
   * @param data
   * @param inputs
   * @returns LiftedFormModel
   */
  setNoEditsInEditMode<T>(data: T, inputs: TLiftedInputTypes<T>[]) {
    return TASK_FORM.setNoEditsInEditMode({
      inputs,
      data,
    });
  },
  /**
   * Sets isTouched property to be true for all the form fields
   * @param liftedFormModel
   * @returns
   */
  markTouchedInputs: <T>(liftedFormModel: TLiftedFormModel<T>) => {
    return TASK_FORM.setTouched(liftedFormModel);
  },

  /**
   * Updates dropdown for input type 'SEARCH' and 'DROPDOWN' with the results params.
   * @param key
   * @param prevLiftedFormModel
   * @param results
   * @returns formModel
   */
  updateDropdownForKey(key: string, prevLiftedFormModel: TLiftedFormModel, results: TDropdownItem[]): TLiftedFormModel {
    const { inputs } = prevLiftedFormModel;
    const updatedInputs = inputs.map((val) => {
      if (key === val.metadata.keyName) {
        switch (val.input.type) {
          case 'DROPDOWN': {
            return TASK_FORM.updateDropdownType(val, { type: 'DROPDOWN', options: [...results] });
          }
          case 'DROPDOWN_WITH_SEARCH': {
            return TASK_FORM.updateDropdownType(val, { type: 'DROPDOWN_WITH_SEARCH', options: [...results] });
          }
          case 'SEARCH': {
            return TASK_FORM.updateSearchType(val, { type: 'SEARCH', options: [...results] });
          }
          default:
            return val;
        }
      }
      return val;
    });
    return {
      ...prevLiftedFormModel,
      inputs: [...updatedInputs],
    };
  },
};
